import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, AFFILIATE } from "../lib/api";
import { trackAffiliateClick, trackNavClick } from "../lib/analytics";

const links = [
  { to: "/", label: "Home" },
  { to: "/hotels", label: "Hotels" },
  { to: "/plan-your-trip", label: "Plan Your Trip" },
  { to: "/things-to-do", label: "Things To Do" },
  { to: "/blog", label: "Blog" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 glass-card shadow-[0_2px_24px_rgba(11,44,91,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-site flex items-center justify-between py-3 md:py-4">
        <Link to="/" data-testid="logo-home-link" className="flex items-center gap-2 group">
          <img
            src={LOGO_URL}
            alt="Visit Bedford"
            className={`h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03] ${
              scrolled ? "" : "drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)]"
            }`}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => trackNavClick(l.label)}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  scrolled
                    ? isActive
                      ? "text-[#0B2C5B] bg-[#E6F6FD]"
                      : "text-[#0B2C5B]/80 hover:text-[#0B2C5B] hover:bg-slate-100"
                    : isActive
                    ? "text-white bg-white/20"
                    : "text-white/90 hover:text-white hover:bg-white/15"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={AFFILIATE.booking}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid="header-check-hotels-cta"
            onClick={() => trackAffiliateClick({ label: "Check Hotel Prices", category: "hotel", destination: AFFILIATE.booking, location: "header" })}
            className="hidden md:inline-flex items-center gap-2 btn-primary-gradient text-white rounded-full px-5 py-2.5 text-sm font-semibold shadow-md hover:-translate-y-0.5 transition-all"
          >
            Check Hotel Prices
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-2 rounded-full ${
              scrolled ? "text-[#0B2C5B] bg-slate-100" : "text-white bg-white/15"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
          <div className="container-site py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl font-semibold ${
                    isActive ? "bg-[#E6F6FD] text-[#0B2C5B]" : "text-[#0B2C5B]/80 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={AFFILIATE.booking}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => trackAffiliateClick({ label: "Check Hotel Prices", category: "hotel", destination: AFFILIATE.booking, location: "mobile-header" })}
              className="mt-2 btn-primary-gradient text-white text-center rounded-full px-5 py-3 font-semibold"
              data-testid="mobile-header-cta"
            >
              Check Hotel Prices
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
